import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { ProductCard } from '@/components/ProductCard';
import { CountdownTimer } from '@/components/CountdownTimer';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HomePage() {
  const [products, setProducts] = useState([]);
  const [drops, setDrops] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Seed data first
        await axios.post(`${API}/seed`);
        
        const [productsRes, dropsRes] = await Promise.all([
          axios.get(`${API}/products?limit=8`),
          axios.get(`${API}/drops`)
        ]);
        setProducts(productsRes.data);
        setDrops(dropsRes.data);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const categories = [
    { 
      name: 'Men', 
      href: '/shop/men',
      image: 'https://images.unsplash.com/photo-1763610452420-71b91bc59160?auto=format&fit=crop&w=800&q=80'
    },
    { 
      name: 'Women', 
      href: '/shop/women',
      image: 'https://images.unsplash.com/photo-1565970350234-0231bd6c6cdd?auto=format&fit=crop&w=800&q=80'
    },
    { 
      name: 'Kids', 
      href: '/shop/kids',
      image: 'https://images.unsplash.com/photo-1708533475717-52289a1c64fc?auto=format&fit=crop&w=800&q=80'
    },
    { 
      name: 'Accessories', 
      href: '/shop/accessories',
      image: 'https://images.unsplash.com/photo-1522713360981-e014ec8de332?auto=format&fit=crop&w=800&q=80'
    },
  ];

  const nextDrop = drops.find(d => d.drop_date && new Date(d.drop_date) > new Date());

  return (
    <div className="pt-16 md:pt-20" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative h-[100vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1624816804260-65ea43f9615e?auto=format&fit=crop&w=1920&q=80')`
          }}
        />
        <div className="hero-overlay absolute inset-0" />
        <div className="relative z-10 text-center container-custom">
          <h1 className="font-['Syne'] text-5xl sm:text-6xl lg:text-8xl font-bold mb-6 animate-slideUp">
            LOST<span className="text-[#FF3B30]">N</span>FOUND
          </h1>
          <p className="text-lg md:text-xl text-neutral-300 mb-8 max-w-xl mx-auto animate-slideUp stagger-1">
            Premium streetwear for the whole family
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slideUp stagger-2">
            <Link to="/shop">
              <Button className="btn-primary rounded-none min-w-[200px]" data-testid="shop-now-btn">
                Shop Now
              </Button>
            </Link>
            <Link to="/drops">
              <Button variant="outline" className="btn-outline rounded-none min-w-[200px]" data-testid="view-drops-btn">
                View Drops
              </Button>
            </Link>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronRight className="rotate-90" size={32} />
        </div>
      </section>

      {/* Categories Bento Grid */}
      <section className="section container-custom">
        <div className="flex items-end justify-between mb-12">
          <div>
            <span className="text-[#FF3B30] uppercase text-xs tracking-widest">Collections</span>
            <h2 className="font-['Syne'] text-3xl md:text-4xl font-bold mt-2">Shop by Category</h2>
          </div>
          <Link to="/shop" className="hidden md:flex items-center space-x-2 uppercase text-xs tracking-widest hover:text-[#FF3B30] transition-colors">
            <span>View All</span>
            <ArrowRight size={16} />
          </Link>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {categories.map((category, idx) => (
            <Link
              key={category.name}
              to={category.href}
              className={`group relative overflow-hidden aspect-[3/4] border border-neutral-800 hover:border-neutral-500 transition-all ${
                idx === 0 ? 'lg:col-span-2 lg:row-span-2' : ''
              }`}
              data-testid={`category-${category.name.toLowerCase()}`}
            >
              <img
                src={category.image}
                alt={category.name}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              <div className="absolute bottom-6 left-6">
                <h3 className="font-['Syne'] text-2xl md:text-3xl font-bold mb-2 group-hover:text-[#FF3B30] transition-colors">
                  {category.name}
                </h3>
                <span className="flex items-center space-x-2 uppercase text-xs tracking-widest opacity-0 group-hover:opacity-100 transition-opacity">
                  <span>Shop Now</span>
                  <ArrowRight size={14} />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Next Drop Section */}
      {nextDrop && (
        <section className="section bg-neutral-900/50">
          <div className="container-custom">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <span className="text-[#FF3B30] uppercase text-xs tracking-widest">Coming Soon</span>
                <h2 className="font-['Syne'] text-3xl md:text-5xl font-bold mt-2 mb-6">
                  {nextDrop.name}
                </h2>
                <p className="text-muted-foreground mb-8 max-w-md">
                  {nextDrop.description}
                </p>
                <CountdownTimer targetDate={nextDrop.drop_date} />
                <div className="mt-8">
                  <Link to="/drops">
                    <Button className="btn-primary rounded-none" data-testid="notify-drop-btn">
                      Get Notified
                    </Button>
                  </Link>
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <div className="aspect-square border border-neutral-800 overflow-hidden">
                  <img
                    src={nextDrop.images?.[0] || 'https://images.unsplash.com/photo-1591047139829-d91aecb6caea?auto=format&fit=crop&w=600&q=80'}
                    alt={nextDrop.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Featured Products */}
      <section className="section container-custom">
        <div className="flex items-end justify-between mb-12">
          <div>
            <span className="text-[#FF3B30] uppercase text-xs tracking-widest">New In</span>
            <h2 className="font-['Syne'] text-3xl md:text-4xl font-bold mt-2">Featured Products</h2>
          </div>
          <Link to="/shop" className="hidden md:flex items-center space-x-2 uppercase text-xs tracking-widest hover:text-[#FF3B30] transition-colors">
            <span>View All</span>
            <ArrowRight size={16} />
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="aspect-[3/4] bg-neutral-900 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {products.slice(0, 8).map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}

        <div className="mt-12 text-center md:hidden">
          <Link to="/shop">
            <Button variant="outline" className="btn-outline rounded-none" data-testid="view-all-mobile">
              View All Products
            </Button>
          </Link>
        </div>
      </section>

      {/* Lookbook Preview */}
      <section className="section bg-neutral-900/50">
        <div className="container-custom">
          <div className="flex items-end justify-between mb-12">
            <div>
              <span className="text-[#FF3B30] uppercase text-xs tracking-widest">Editorial</span>
              <h2 className="font-['Syne'] text-3xl md:text-4xl font-bold mt-2">Lookbook</h2>
            </div>
            <Link to="/lookbook" className="hidden md:flex items-center space-x-2 uppercase text-xs tracking-widest hover:text-[#FF3B30] transition-colors">
              <span>View Full</span>
              <ArrowRight size={16} />
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div className="aspect-[3/4] overflow-hidden border border-neutral-800 group">
              <img
                src="https://images.unsplash.com/photo-1531035787806-3b47c2fad356?auto=format&fit=crop&w=800&q=80"
                alt="Lookbook 1"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </div>
            <div className="aspect-[3/4] overflow-hidden border border-neutral-800 group">
              <img
                src="https://images.unsplash.com/photo-1663518756516-5e8ce39e412f?auto=format&fit=crop&w=800&q=80"
                alt="Lookbook 2"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </div>
            <div className="aspect-[3/4] overflow-hidden border border-neutral-800 group">
              <img
                src="https://images.unsplash.com/photo-1611618951877-1d8d1ccf1899?auto=format&fit=crop&w=800&q=80"
                alt="Lookbook 3"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </div>
          </div>

          <div className="mt-12 text-center md:hidden">
            <Link to="/lookbook">
              <Button variant="outline" className="btn-outline rounded-none" data-testid="view-lookbook-mobile">
                View Full Lookbook
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Brand Story Banner */}
      <section className="section">
        <div className="container-custom">
          <div className="relative border border-neutral-800 p-8 md:p-16 text-center">
            <span className="text-[#FF3B30] uppercase text-xs tracking-widest">Our Story</span>
            <h2 className="font-['Syne'] text-2xl md:text-4xl font-bold mt-4 mb-6 max-w-2xl mx-auto">
              Streetwear that brings the family together
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto mb-8">
              Founded with a vision to create premium quality streetwear that the whole family can rock together. 
              No compromises on style, no compromises on quality.
            </p>
            <Link to="/about">
              <Button className="btn-primary rounded-none" data-testid="learn-more-btn">
                Learn More
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
